import '@app/App.css';
import CompozzSpinner from '@comps/ui/CompozzSpinner';
import { DialogContent } from '@comps/ui/dialog';
import { CompozzError, isCompozzError } from '@dto/CompozzError';
import { CompozzIcon, SaxIconType } from '@dto/CompozzIcon';
import { LoadTemplateRequest, Workspace } from '@dto/workspaces';
import { IsDarkColor } from '@utils/colors';
import { BASE_URL } from '@utils/constants';
import { CIcon, getRandomIcon } from '@utils/icons';
import { toastError, toastSuccess } from '@utils/toasts';
import { TW_CompozzSpinnerContainer, TW_HomeTemplateItem, TW_HomeWorkspacesItemIcon, TW_HomeWorkspacesItemIconContainer, TW_HomeWorkspacesItemText, TW_HomeWorkspacesItemTextDescription, TW_HomeWorkspacesItemTextTitle, TW_HomeWorkspacesTemplateDialog } from '@utils/twClasses';
import { post } from '@utils/utils';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { translate } from 'src/i18n/i18n';

type Template = {
  name: string;
  description: string;
  icon: CompozzIcon;
  color: string;
  id: string;
}

type WorkspaceTemplatesDialogContentProps = {
  onWorkspaceCreated: (workspace: Workspace) => void;
}

function WorkspaceTemplatesDialogContent({ onWorkspaceCreated }: WorkspaceTemplatesDialogContentProps) {

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const templates: Template[] = [
    {
      name: t('template.crm.name'),
      description: t('template.crm.description'),
      icon: {
        name: 'crown-1',
        type: SaxIconType,
      },
      color: '#B11D85',
      id: 'customer_management',
    },
    {
      name: t('template.erp.name'),
      description: t('template.erp.description'),
      icon: {
        name: 'box-time',
        type: SaxIconType,
      },
      color: '#BFDFFF',
      id: 'command_management',
    },
    {
      name: t('template.project.management.name'),
      description: t('template.project.management.description'),
      icon: {
        name: 'command',
        type: SaxIconType,
      },
      color: '#00CC99',
      id: 'project_management',
    }
  ];

  function handleTemplateLoad(id: string) {

    const loadTemplateRequest: LoadTemplateRequest = {
      templateId: id
    }
    setLoading(true);
    post<any>(`${BASE_URL}/model/workspaces/template`, loadTemplateRequest).then((response) => {
      if (response) {
        if (isCompozzError(response)) {
          const cError = response as CompozzError;
          if (cError.i18n) {
            toastError(translate(t, cError.i18n, cError.params));
          } else {
            toastError(cError.message);
          }
        } else {
          const workspace = response as Workspace;
          onWorkspaceCreated(workspace);
          toastSuccess(t("compozz.file.import.success"));
        }
      }
      else {
        toastError(t("compozz.file.import.error"));
      }
    }).catch((error) => {
      console.error(error);
      toastError(t("compozz.file.import.error"));
    }).finally(() => {
      setLoading(false);
    })
  }

  if (loading) {
    return (
      <div className={TW_CompozzSpinnerContainer}>
        <CompozzSpinner />
      </div>
    )
  }

  return (
    <DialogContent className={TW_HomeWorkspacesTemplateDialog}>
      {templates.map((template) => {
        const iconBgColor = template.color;
        const iconColor = IsDarkColor(iconBgColor) ? "#ffffff" : "#000000";
        return (
          <div key={template.id} className={TW_HomeTemplateItem} onClick={() => handleTemplateLoad(template.id)}>
            <div className={TW_HomeWorkspacesItemIconContainer} style={{ backgroundColor: iconBgColor }}>
              <CIcon compozzIcon={template.icon ?? getRandomIcon()} className={TW_HomeWorkspacesItemIcon} color={iconColor} />
            </div>
            <div className={TW_HomeWorkspacesItemText}>
              <p className={TW_HomeWorkspacesItemTextTitle}>{template.name}</p>
              <p className={TW_HomeWorkspacesItemTextDescription}>{template.description}</p>
            </div>
          </div>
        )
      })}

    </DialogContent>
  );
};

export default WorkspaceTemplatesDialogContent;